import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import InputSelect from "../../../components/InputSelect";
import Input from "../../../components/Input";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import { renderError } from "../../../helpers/errors";
import api from "../../../services/api";
import { createOptions } from '../../../helpers/selects';
import { convertIntToMoney, convertMoenyToInt, getBase64 } from "../../../helpers/conversions";
import CustomizationSection from "./CustomizationSection";
import ParamSection from "./ParamSection";
import { Calculation } from "./calculationPrice";
import { calculateCustomizationTotal, calculateSizeCustomizationTotal } from "./customizationCalculate";
import { ModalDelete } from "../../../components/ModalDelete";
import { toast } from "react-toastify";
import SelectBillingCompany from "../../../components/selects/SelectBillingCompany";
import SelectProduct from "../../../components/selects/SelectProduct";
import { canSeeProductCost, canSeeProductSupplier, canSeeStock, hasPermission, hasPlanFeature } from "../../../services/auth";
import { confirmPlanFeature } from "../../../services/plan";
import InputTextarea from "../../../components/InputTextarea";
import { ModalProductList } from "../ModalProductList";
import { ModalGenerateMockup } from "./ModalGenerateMockup";
import MockupPanel from "./MockupPanel";
import PlanFeatureUpgradeModal from "../../../components/PlanFeatureUpgradeModal";
import ProductPriceAlert from "../../../components/ProductPriceAlert";
import Spinner from "../../../components/Spinner";
import {
    extractSubproductDetailFromSearchResource,
    getProductGalleryImageUrls,
    getSubproductPreviewImageUrl,
    getSubproductSelectThumbnailUrl,
    getSubproductVariantOwnImageUrl,
    isLikelyNonImageMockupSrc,
    productNeedsSubproductImageDetail
} from "./subproductImage";
import { productHasSpotComponents } from "./spotComponents";
import ModalAvailablePrices from "./ModalAvailablePrices";
import { hasSomarcasEngraving } from "./somarcasEngraving";
import "./styles.scss";

const COST_NOT_FOUND_ERROR = 'Custo não encontrado no fornecedor para a quantidade informada.';

const calculationTypeOptions = [
    { label: 'Pré-cadastrado', value: 'automatic' },
    { label: 'Manual', value: 'manual' }
];

const canPersistMockup = (q) => {
    if (!q) return false;
    if (q.type === 'PF') return !!q.customer_id;
    if (q.type === 'PJ') return !!q.company_id;
    return !!(q.customer_id || q.company_id);
};

const mergeProductDetail = (row, full) => {
    const subproducts =
        Array.isArray(full.subproducts) && full.subproducts.length > 0
            ? full.subproducts.map((fs) => {
                  const old = (row.subproducts || []).find((p) => p.id === fs.id);
                  if (!old) return fs;
                  return {
                      ...fs,
                      image: fs.image ?? old.image,
                      product: fs.product ?? old.product,
                      color: fs.color ?? old.color,
                      sku: fs.sku ?? old.sku,
                      stock: fs.stock != null ? fs.stock : old.stock
                  };
              })
            : row.subproducts;

    return { ...row, ...full, subproducts };
};

const ModalProduct = ({close, quote, itemToEdit, setItemToEdit, selectedBillingCompany, setSelectedBillingCompany, getQuote, updateQuote}) => {
    const [loading, setLoading] = useState(true);
    const [gettingCustomizations, setGettingCustomizations] = useState(true);

    const [params, setParams] = useState([]);
    const [customizations, setCustomizations] = useState([]);
    const [products, setProducts] = useState([]);
    const [subproducts, setSubproducts] = useState([]);

    const [gettingPrice, setGettingPrice] = useState(false);
    
    const [product, setProduct] = useState(null);
    const [stock, setStock] = useState(0);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedSubproduct, setSelectedSubproduct] = useState(null);

    const [companiesProduct, setCompaniesProduct] = useState([]);
    const [selectedCompanyProduct, setSelectedCompanyProduct] = useState(null);

    const [productCalculationType, setProductCalculationType] = useState('automatic');
    const [productQuantity, setProductQuantity] = useState(1);
    const [productQuantityColors, setProductQuantityColors] = useState(1);
    const [productUnitCost, setProductUnitCost] = useState('0,00');
    const [productUnitCostError, setProductUnitCostError] = useState('');
    const [productTotalCost, setProductTotalCost] = useState('0,00');
    const [productSupplierCost, setProductSupplierCost] = useState(0);

    const [productComments, setProductComments] = useState('');

    const [productCustomizations, setProductCustomizations] = useState([]);
    const [productParams, setProductParams] = useState([]);

    const [formulaPrice, setFormulaPrice] = useState('');
    const [formulaCommission, setFormulaCommission] = useState('');

    const [salePrice, setSalePrice] = useState('R$ 0,00');
    const [totalSalePrice, setTotalSalePrice] = useState('R$ 0,00');
    const [commission, setCommission] = useState('R$ 0,00');

    const [gettingBillingCompanies, setGettingBillingCompanies] = useState(true);
    const [billingCompanies, setBillingCompanies] = useState([]);

    const [showDelete, setShowDelete] = useState(false);

    const [subproductPrices, setSubproductPrices] = useState([]);
    const [showAvailablePrices, setShowAvailablePrices] = useState(false);

    const [showModal, setShowModal] = useState(false);

    const [serverMockupUrl, setServerMockupUrl] = useState(null);
    const [mockupItemId, setMockupItemId] = useState(itemToEdit?.id ?? null);
    const [mockupBase64, setMockupBase64] = useState(null);
    const [mockupFileName, setMockupFileName] = useState('');
    const [mockupUploading, setMockupUploading] = useState(false);
    const [mockupPendingDelete, setMockupPendingDelete] = useState(false);
    const [showMockupDelete, setShowMockupDelete] = useState(false);
    const [showGenerateMockup, setShowGenerateMockup] = useState(false);
    const [showMockupUpgrade, setShowMockupUpgrade] = useState(false);
    // Escolhas do gerador para a amostra exibida; null = arquivo enviado.
    const [mockupInfo, setMockupInfo] = useState(itemToEdit?.product_mock ?? null);
    const [pendingMockupMeta, setPendingMockupMeta] = useState(null);

    const hasPermissionToEditBudget = hasPermission('edit_budget');

    /**
     * Snapshot da busca de preço por subproduto, sempre amarrado ao id da cor selecionada
     * (evita imagem da cor errada por resposta atrasada ao trocar variante).
     */
    const [subproductSearchMeta, setSubproductSearchMeta] = useState(null);

    const selectedSubproductRef = useRef(selectedSubproduct);
    const selectedProductRef = useRef(selectedProduct);
    const productDetailFetchAttemptedRef = useRef(new Set());
    // O SelectProduct reemite as linhas da busca a cada pesquisa; sem reaplicar
    // o detalhe, o produto selecionado perde as gravações da Spot, que a busca
    // não traz mais (BDM-1157), e a seção fica presa no "carregando".
    const productDetailsRef = useRef({});
    const handleSearchProducts = useCallback((rows) => {
        setProducts(rows.map((row) => {
            const full = productDetailsRef.current[row.id];
            return full ? mergeProductDetail(row, full) : row;
        }));
    }, []);

    useEffect(() => {
        selectedSubproductRef.current = selectedSubproduct;
    }, [selectedSubproduct]);

    useEffect(() => {
        selectedProductRef.current = selectedProduct;
    }, [selectedProduct]);

    useEffect(() => {
        productDetailFetchAttemptedRef.current.clear();
    }, [selectedProduct]);

    const itemHasSpotCustomizations = useMemo(
        () => itemToEdit?.budget_item_customizations?.some((customization) => customization?.table === 'spot') ?? false,
        [itemToEdit]
    );

    /**
     * Subproduto da cor no payload do produto (SelectProduct); em edição, mescla com o subproduto
     * do item se a lista do produto ainda não tiver `image` preenchida.
     */
    const selectedSubproductEntity = useMemo(() => {
        const fromList = product?.subproducts?.find((s) => s.id === selectedSubproduct);
        const fromEdit =
            itemToEdit?.subproduct_id === selectedSubproduct ? itemToEdit.subproduct : null;
        if (!fromList && !fromEdit) return null;
        if (!fromList) return fromEdit;
        if (!fromEdit) return fromList;

        const listHasVariantPhoto = getSubproductVariantOwnImageUrl(fromList, product);
        if (listHasVariantPhoto) return fromList;

        return {
            ...fromList,
            image: fromEdit.image ?? fromList.image,
            product: fromList.product ?? fromEdit.product
        };
    }, [product, selectedSubproduct, itemToEdit]);

    const parentProductForImage = product ?? itemToEdit?.subproduct?.product ?? null;

    const subproductSearchDetailForSelection =
        subproductSearchMeta?.subproductId === selectedSubproduct
            ? subproductSearchMeta.detail
            : null;

    const colorPreviewUrl = useMemo(() => {
        const parent = parentProductForImage;
        const tryUrl = (sp) => (sp ? getSubproductPreviewImageUrl(sp, parent) : null);

        const fromEdit =
            itemToEdit?.subproduct_id === selectedSubproduct ? itemToEdit.subproduct : null;

        return (
            tryUrl(selectedSubproductEntity) ||
            tryUrl(subproductSearchDetailForSelection) ||
            tryUrl(fromEdit) ||
            null
        );
    }, [
        parentProductForImage,
        itemToEdit,
        selectedSubproduct,
        selectedSubproductEntity,
        subproductSearchDetailForSelection
    ]);

    /**
     * Fotos candidatas a base do mockup com IA: a da cor selecionada
     * (destacada) + a galeria do produto, sem duplicar URL.
     */
    const mockupImageOptions = useMemo(() => {
        const options = [];
        const seen = new Set();
        const push = (url, isColor) => {
            if (!url || seen.has(url)) return;
            seen.add(url);
            options.push({ url, isColor });
        };
        push(colorPreviewUrl, true);
        getProductGalleryImageUrls(parentProductForImage).forEach((url) => push(url, false));
        return options;
    }, [colorPreviewUrl, parentProductForImage]);

    const mockupDisplaySrc = (!mockupPendingDelete && serverMockupUrl) || mockupBase64;
    const mockupIsPdf = useMemo(
        () => isLikelyNonImageMockupSrc(mockupDisplaySrc),
        [mockupDisplaySrc]
    );

    const selectedSubproductThumbnailUrl = useMemo(
        () =>
            selectedSubproduct && selectedSubproductEntity
                ? getSubproductSelectThumbnailUrl(selectedSubproductEntity, product)
                : null,
        [selectedSubproduct, selectedSubproductEntity, product]
    );

    useEffect(() => {
        setMockupBase64(null);
        setMockupFileName('');
        setMockupPendingDelete(false);
        setPendingMockupMeta(null);
        setServerMockupUrl(itemToEdit?.product_mock_image_url ?? null);
        setMockupInfo(itemToEdit?.product_mock ?? null);
    }, [itemToEdit]);

    useEffect(() => {
        setMockupItemId(itemToEdit?.id ?? null);
    }, [itemToEdit]);

    const downloadMockupPdf = useCallback(async () => {
        if (!serverMockupUrl || !mockupItemId) return;

        try {
            const res = await api.get(`budget/item/${mockupItemId}/product-mock/pdf`, {
                responseType: 'blob'
            });
            const disposition = res.headers?.['content-disposition'] ?? '';
            const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
            const plainMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i);
            const filename = utf8Match
                ? decodeURIComponent(utf8Match[1])
                : (plainMatch?.[1]?.trim() || 'amostra-virtual.pdf');

            const blobUrl = URL.createObjectURL(
                new Blob([res.data], { type: res.data?.type || 'application/pdf' })
            );
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (err) {
            renderError(err);
        }
    }, [mockupItemId, serverMockupUrl]);

    const uploadMockupNow = useCallback(async (itemId, base64, meta = null) => {
        if (!itemId || !base64) return;

        setMockupUploading(true);
        try {
            const { logotype, ...mock } = meta ?? {};
            const res = await api.post(`budget/item/${itemId}/product-mock`, {
                image: base64,
                mock: meta ? mock : null
            });
            const resource = res.data.resource ?? {};
            const url =
                resource.product_mock_image_url ??
                resource.mockup_url ??
                resource.url ??
                null;
            setMockupItemId(itemId);
            if (url) setServerMockupUrl(url);
            setMockupInfo(resource.product_mock ?? null);
            if (url && setItemToEdit) {
                setItemToEdit((current) => {
                    if (!current || current.id !== itemId) return current;
                    return {
                        ...current,
                        product_mock_image_url: url,
                        product_mock: resource.product_mock ?? null
                    };
                });
            }
            setMockupBase64(null);
            setPendingMockupMeta(null);
            setMockupFileName('');
            setMockupPendingDelete(false);
            if (getQuote) {
                await getQuote();
            }
        } catch (err) {
            renderError(err);
        } finally {
            setMockupUploading(false);
        }
    }, [getQuote, setItemToEdit]);

    const handleMockupBase64Change = useCallback(async (base64, meta = null) => {
        setMockupBase64(base64);
        setPendingMockupMeta(meta);
        setMockupInfo(meta);
        if (base64) {
            setMockupPendingDelete(false);
        }
        if (!base64) return;
        if (!selectedProduct) {
            toast.error('Selecione o produto antes de anexar o mockup.');
            setMockupBase64(null);
            setPendingMockupMeta(null);
            setMockupFileName('');
            return;
        }
        if (mockupItemId) {
            await uploadMockupNow(mockupItemId, base64, meta);
        }
    }, [mockupItemId, selectedProduct, uploadMockupNow]);

    /**
     * Amostra aprovada no fluxo de IA entra pelo mesmo caminho do upload
     * manual: pendente até salvar o item, ou upload imediato se o item existe.
     */
    const applyGeneratedMockup = useCallback(async (base64, meta) => {
        setShowGenerateMockup(false);
        setMockupFileName('amostra-virtual.png');
        await handleMockupBase64Change(base64, meta ?? null);
    }, [handleMockupBase64Change]);

    const showMockupInlineSavingEarly = Boolean(
        loading && (!!mockupBase64 || mockupPendingDelete)
    );

    const canDropVirtualSample =
        hasPermissionToEditBudget && !!selectedProduct && !showMockupInlineSavingEarly && !mockupUploading;

    const onVirtualSampleDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (!file) return;
            if (!selectedProduct) {
                toast.error('Selecione o produto antes de anexar o mockup.');
                return;
            }
            setMockupFileName(file.name);
            getBase64(file, (res) => {
                handleMockupBase64Change(res);
            });
        },
        [selectedProduct, handleMockupBase64Change]
    );

    const { getRootProps, getInputProps, isDragActive, open: openMockupFileDialog } = useDropzone({
        onDropAccepted: onVirtualSampleDrop,
        onDropRejected: () => {
            toast.error('Formato inválido. Use JPEG, PNG, GIF ou SVG.');
        },
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/svg+xml': ['.svg']
        },
        maxFiles: 1,
        disabled: !canDropVirtualSample,
        // O clique na lateral amplia a imagem; o seletor de arquivo abre pelo
        // link "envie uma arte pronta".
        noClick: true,
        noKeyboard: true,
        // File System Access API (default no v14) monta o filtro do diálogo
        // errado no Chrome (só oferece o primeiro tipo); o input clássico
        // respeita o accept completo.
        useFsAccessApi: false
    });

    const confirmRemoveMockup = () => {
        if (serverMockupUrl && canPersistMockup(quote)) {
            setMockupPendingDelete(true);
        } else if (serverMockupUrl && !canPersistMockup(quote)) {
            toast.info('Associe um cliente ao orçamento para poder remover o mockup salvo.');
        }
        setMockupBase64(null);
        setPendingMockupMeta(null);
        setMockupFileName('');
        setShowMockupDelete(false);
    };

    /**
     * POST/DELETE do mockup só após o item do orçamento salvar (Adicionar / Atualizar).
     */
    const applyPendingMockupAfterItemSave = useCallback(async (itemId, pendingBase64, pendingDelete, pendingMeta) => {
        if (!itemId || !canPersistMockup(quote)) {
            if (pendingBase64 || pendingDelete) {
                toast.info(
                    'Defina o cliente no orçamento para gravar o mockup (ou a remoção) no servidor.'
                );
            }
            return;
        }
        if (pendingDelete) {
            await api.post(`budget/item/${itemId}/product-mock`, {
                image: null
            });
            setServerMockupUrl(null);
            setMockupInfo(null);
            setMockupItemId(null);
            if (setItemToEdit) {
                setItemToEdit((current) => {
                    if (!current || current.id !== itemId) return current;
                    return {
                        ...current,
                        product_mock_image_url: null,
                        product_mock: null
                    };
                });
            }
            setMockupPendingDelete(false);
            setMockupBase64(null);
            setMockupFileName('');
            if (getQuote) {
                await getQuote();
            }
            return;
        }
        if (pendingBase64) {
            await uploadMockupNow(itemId, pendingBase64, pendingMeta);
        }
    }, [getQuote, setItemToEdit, uploadMockupNow]);

    const hasCalculationFeature = hasPlanFeature('calculation');

    useEffect(() => {
        getProduct();
        getParams();
        getCustomizations();
        getCompaniesProduct();
        getBillingCompanies();
    }, []);

    useEffect(() => {
        getSubproducts();
    }, [products, selectedProduct]);

    /**
     * Só busca cadastro completo quando o payload atual (search / item) não resolve URL da variante.
     */
    useEffect(() => {
        if (!selectedProduct) return;
        const row =
            products.find((p) => p.id === selectedProduct) ||
            (itemToEdit?.subproduct?.product_id === selectedProduct && itemToEdit.subproduct?.product
                ? itemToEdit.subproduct.product
                : null);
        const needsSubproductImages = row ? productNeedsSubproductImageDetail(row) : false;
        const needsSpotComponents =
            !Array.isArray(row?.spot_components) && (itemHasSpotCustomizations || productHasSpotComponents(row));
        if (!row?.id || (!needsSubproductImages && !needsSpotComponents)) return;

        const productId = selectedProduct;
        if (productDetailFetchAttemptedRef.current.has(productId)) return;
        productDetailFetchAttemptedRef.current.add(productId);

        // Sem cancelar pela troca de `products`: a busca reemite a lista durante
        // o fetch, e a resposta descartada não seria pedida de novo (o id já
        // está em productDetailFetchAttemptedRef). Mesclar o detalhe é idempotente.
        api.get(`/product/${productId}`)
            .then((res) => {
                const full = res.data.resource;
                if (!full?.id) return;
                productDetailsRef.current[full.id] = full;
                if (selectedProductRef.current !== productId) return;
                setProducts((prev) => {
                    const i = prev.findIndex((p) => p.id === full.id);
                    if (i === -1) {
                        return [...prev, full];
                    }
                    const next = [...prev];
                    next[i] = mergeProductDetail(next[i], full);
                    return next;
                });
            })
            .catch(() => {
                productDetailFetchAttemptedRef.current.delete(productId);
            });
    }, [selectedProduct, products, itemToEdit, itemHasSpotCustomizations]);

    useEffect(() => {
        getPrices();
    }, [selectedSubproduct]);

    useEffect(() => {
        setFirstSubproductAsSelected();
    }, [subproducts]);
    
    useEffect(() => {
        getProductUnitCost();
        getCompaniesProduct();
    }, [subproductPrices]);

    const getBillingCompanies = () => {
        setGettingBillingCompanies(true);

        api.get(`/company?filters[0][type][contain]=billing`).then(res => {
            setBillingCompanies(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setGettingBillingCompanies(false));
    }

    const setFirstSubproductAsSelected = () => {
        if(subproducts.length > 0 && subproducts[0].color === null){
            setSelectedSubproduct(subproducts[0].id);
        }
    }
    
    const handleProductChange = productId => {
        setSelectedSubproduct(null);
        setCompaniesProduct([]);
        setSelectedCompanyProduct(null);
        setSelectedProduct(productId);
    }

    const getSubproducts = () => {
        let toSubproducts = [];
        let product = products.filter(product => product.id === selectedProduct)[0];
        if(!product) return;
        const showStock = canSeeStock();
        product.subproducts.map((subproduct) => {
            const colorName =
                subproduct.color?.name?.trim() ||
                (typeof subproduct.color_name === 'string' ? subproduct.color_name.trim() : '');
            const skuPart = subproduct.sku != null && String(subproduct.sku).trim() !== '' ? subproduct.sku : '';
            const stockVal =
                subproduct.stock != null && !Number.isNaN(Number(subproduct.stock))
                    ? Number(subproduct.stock).toLocaleString('pt-BR')
                    : '0';
            let desc = `Cor única`;
            if(colorName || skuPart){
                const parts = colorName ? [colorName, skuPart || '—'] : [skuPart];
                if(showStock) parts.push(`${stockVal} unid.`);
                desc = parts.join(' - ');
            }
            toSubproducts.push({
                id: subproduct.id,
                color: subproduct.color,
                desc
            });
        });
        setSubproducts(toSubproducts);
    }

    useEffect(() => {
        calculateProductTotalCost();
    }, [productQuantity, productUnitCost]);

    useEffect(() => {
        getProductUnitCost();
    }, [selectedCompanyProduct, productQuantity, productCalculationType, selectedSubproduct, selectedProduct, products]);

    useEffect(() => {
        calculatePrice();
    }, [billingCompanies, selectedBillingCompany, productQuantity, productUnitCost, productCustomizations, productParams, product, selectedSubproduct]);

    useEffect(() => {
        getTotalSalePrice();
    }, [productQuantity, salePrice]);

    useEffect(() => {
        getProductParams();
    }, [params])

    useEffect(() => {
        getSelectedProduct();
    }, [selectedProduct, products]);

    const getSelectedProduct = () => {
        const fromCatalog = products.find((p) => p.id === selectedProduct);
        if (fromCatalog) {
            setProduct(fromCatalog);
            return;
        }
        if (itemToEdit?.subproduct?.product_id === selectedProduct && itemToEdit.subproduct?.product) {
            setProduct(itemToEdit.subproduct.product);
            return;
        }
        setProduct(null);
    };

    const getProduct = () => {
        if(!itemToEdit) return;
        setSelectedProduct(itemToEdit.subproduct.product_id);
        setSelectedSubproduct(itemToEdit.subproduct_id);
        setSelectedCompanyProduct(itemToEdit.company_id);
        setProductCalculationType(itemToEdit.calculation_type);
        setProductQuantity(itemToEdit.quantity);
        setProductComments(itemToEdit.comments ?? '');
        setProductQuantityColors(itemToEdit.quantity_colors ?? 1);
        setProductUnitCost( `R$ ${convertIntToMoney(itemToEdit.cost)}` );
        if (itemToEdit.price != null) {
            setSalePrice(`R$ ${convertIntToMoney(itemToEdit.price)}`);
        }

        getProductCustomizations(itemToEdit.budget_item_customizations);
    }

    const getTotalSalePrice = () => {
        let quantity = productQuantity;
        let price = convertMoenyToInt(salePrice);
        let total = price * quantity;
        total = `R$ ${convertIntToMoney(total)}`;
        setTotalSalePrice(total);
    }

    const calculatePrice = () => {
        if (!hasCalculationFeature) return;
        if(billingCompanies.length === 0){
            return;
        }
        if (!selectedBillingCompany || !product) {
            return;
        }
        const taxPercentage = getTaxPercentage();
        const calculation = new Calculation(
            formulaPrice, 
            formulaCommission, 
            productQuantity, 
            productUnitCost, 
            productCustomizations, 
            productParams,
            taxPercentage,
            params,
            product
        );

        const toSalePrice = calculation.getSalePrice();
        const toCommission = calculation.getCommission();

        setSalePrice( `R$ ${convertIntToMoney(toSalePrice)}`);
        setCommission( `R$ ${convertIntToMoney(toCommission)}`);
    }

    const getTaxPercentage = () => {
        const company = billingCompanies.find(
            (billingCompany) => billingCompany.id === selectedBillingCompany
        );
        return company?.tax_percentage ?? 0;
    }

    const addProductCustomization = () => {
        if (!product) return;
        const toProductCustomization = {
            calculationType: 'automatic',
            supplierSelectionMode: 'automatic',
            selectedCustomization: null,
            selectedCompanyCustomization: null,
            quantityColors: productQuantityColors,
            quantity: productQuantity,
            customizationUnitCost: `R$ 0,00`,
            customizationTotalCost: `R$ 0,00`,
            table: productHasSpotComponents(product)
                ? 'spot'
                : hasSomarcasEngraving(product)
                    ? 'somarcas'
                    : 'registered',
            product_id: product.id,
            // campos do cálculo por tamanho (DTF)
            selectedSizeId: null,
            engravingsPerItem: 1,
            artWidthMm: '',
            artHeightMm: '',
            spacingMm: null,
            units: null,
            fixedFee: null,
            applicationCost: null
        };

        setProductCustomizations(prev => [...prev, toProductCustomization]);
    }

    // Produto da SM Revenda quase sempre sai com gravação: o item novo já
    // começa com ela, e quem não quiser remove (BDM-1303). Uma vez por
    // produto, para não voltar depois de removida; item em edição fica como
    // foi salvo. Trocar para produto de outro fornecedor tira a gravação da
    // tabela da SM, que nele sairia zerada.
    const autoEngravingProductRef = useRef(null);

    useEffect(() => {
        if (itemToEdit || !hasCalculationFeature || !product) return;

        if (!hasSomarcasEngraving(product)) {
            if (productCustomizations.some(customization => customization.table === 'somarcas')) {
                setProductCustomizations(prev => prev.filter(customization => customization.table !== 'somarcas'));
            }
            return;
        }

        if (autoEngravingProductRef.current === product.id) return;

        autoEngravingProductRef.current = product.id;

        if (productCustomizations.length > 0) return;

        addProductCustomization();
    }, [product, itemToEdit]);

    const getCompaniesProduct = async () => {
        let toCompaniesProduct = [];

        subproductPrices.map(price => {
            toCompaniesProduct.push(price.company);
        });
        setCompaniesProduct(toCompaniesProduct);
    }

    const getPrices = () => {
        if (!selectedSubproduct) {
            setSubproductSearchMeta(null);
            setSubproductPrices([]);
            setStock(0);
            return;
        }

        const spId = selectedSubproduct;
        setSubproductSearchMeta(null);
        setGettingPrice(true);

        api.get(`/subproduct/${spId}/search`)
            .then((res) => {
                if (selectedSubproductRef.current !== spId) return;
                const resource = res.data.resource ?? {};
                const detail = extractSubproductDetailFromSearchResource(resource, spId);
                setSubproductSearchMeta({ subproductId: spId, detail });
                const prices = [];
                (resource.prices ?? []).map((price) => {
                    prices.push(price);
                });
                setStock(resource.stock ?? 0);
                setSubproductPrices(prices);
            })
            .catch((error) => {
                if (selectedSubproductRef.current !== spId) return;
                console.error(error);
                renderError(error);
                setSubproductSearchMeta(null);
                setSubproductPrices([]);
            })
            .finally(() => {
                if (selectedSubproductRef.current === spId) {
                    setGettingPrice(false);
                }
            });
    };
    
    const getParams = () => {
        api.get('/param').then(res => {
            let toParams = [];
            res.data.resources.map(resource => {
                if(resource.type === 'price_calculation'){
                    setFormulaPrice(resource.value);
                    return;
                }
                if(resource.type === 'commission_calculation'){
                    setFormulaCommission(resource.value);
                    return;
                }
                toParams.push(resource);
            })
            setParams(toParams);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const getProductCustomizations = (currentProductCustomizations) => {
        let toProductCustomizations = [];
        
        currentProductCustomizations.map(current => {
            let total = 0;
            if(current.table === 'registered'){
                total = current.customization?.calculation_per === 'size'
                    ? calculateSizeCustomizationTotal(current.units ?? 0, current.cost, current.fixed_fee, current.application_cost, current.quantity)
                    : calculateCustomizationTotal(
                        current.quantity,
                        current.cost,
                        current.customization.calculation_per
                    );
            }
            toProductCustomizations.push({
                calculationType: current.calculation_type,
                supplierSelectionMode: current.supplier_selection_mode,
                selectedCustomization: current.customization_id,
                selectedSupplierCustomization: current.company_id ? current.company_id : current.customer_id,
                selectedSupplierTypeCustomization: current.company_id ? "PJ" : "PF",
                quantityColors: current.quantity_colors,
                quantity: current.quantity,
                customizationUnitCost: `R$ ${convertIntToMoney( current.cost )}`,
                customizationTotalCost: `R$ ${convertIntToMoney( total )}`,
                spot_component: current?.spot_component ?? null,
                spot_location: current?.spot_location ?? null,
                spot_area: current?.spot_area ?? null,
                spot_customization: current?.spot_customization ?? null,
                spot_colors: current?.spot_colors ?? null,
                table: current.table,
                deleted: current?.deleted ?? false,
                customizationName: current?.customization?.name ?? null,
                supplierName: current.company_id ? current?.company?.trade_name : current?.customer?.name ?? null,
                company: current.customization?.companies.length > 0 ? current.customization?.companies[0].trade_name : null,
                // campos do cálculo por tamanho (DTF)
                selectedSizeId: current?.customization_size_id ?? null,
                engravingsPerItem: current?.engravings_per_item ?? null,
                artWidthMm: current?.art_width_mm ?? '',
                artHeightMm: current?.art_height_mm ?? '',
                spacingMm: current?.spacing_mm ?? null,
                units: current?.units ?? null,
                fixedFee: current?.fixed_fee ?? null,
                applicationCost: current?.application_cost ?? null,
            });
        });
        setProductCustomizations(toProductCustomizations);
    }

    const getProductParams = () => {
        let toProductParams = [];
        let currentParams = itemToEdit?.params ? JSON.parse( itemToEdit.params ) : [];
        params.map(param => {
            let currentParam = currentParams.filter(currentParam => currentParam.id === param.id);
            if(currentParam.length === 0){
                toProductParams.push({
                    type: param.type,
                    name: param.name,
                    options: param.options,
                    position: param.position,
                    param_id: param.param_id,
                    id: param.id,
                    formula_total_real: param.formula_total_real,
                    formula_total_quote: param.formula_total_quote,
                    value: param.type === 'percentage' && param.value ? param.value : null,
                    values: []
                });
            } else {
                toProductParams.push(currentParam[0]);
            }
        });

        setProductParams(toProductParams);
    }

    const getCustomizations = () => {
        setGettingCustomizations(true);

        api.get('/customization').then(res => {
            setCustomizations(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).finally(() => setGettingCustomizations(false));
    }
    
    const calculateProductTotalCost = () => {
        let quantity = productQuantity;
        let cost = productUnitCost;
        cost = convertMoenyToInt(cost);
        let total = cost * quantity;
        total = `R$ ${convertIntToMoney(total)}`;
        setProductTotalCost(total);
    }

    const getProductUnitCost = async () => {
        if (!hasCalculationFeature) return;
        setProductUnitCostError('');

        let error = COST_NOT_FOUND_ERROR;

        if(!selectedSubproduct){
            error = 'Informe o produto e a cor para obter o custo.';
        }

        let cost = 0;
        let narrowest = null;

        // Entre faixas que cobrem a mesma quantidade vale a de menor intervalo,
        // a mesma regra do Subproduct::getPrice no backend: uma faixa ampla
        // (1 a 9999999) não pode atropelar a tabela de quantidade (BDM-1160).
        subproductPrices.forEach(price => {
            if (productQuantity < price.init_quantity || productQuantity > price.final_quantity) return;

            const range = price.final_quantity - price.init_quantity;

            if (narrowest === null || range < narrowest) {
                narrowest = range;
                error = '';
                cost = price.price;
            }
        });

        setProductSupplierCost(cost);

        if(productCalculationType === 'manual'){
            const manualCost =
                itemToEdit?.cost != null
                    ? itemToEdit.cost
                    : convertMoenyToInt(productUnitCost || 'R$ 0,00');
            setProductUnitCost( `R$ ${convertIntToMoney(manualCost)}` );
        } else {
            setProductUnitCost( `R$ ${convertIntToMoney(cost)}` );
            setProductUnitCostError(error);
        }
    }

    const updateProduct = (create = false) => {
        const validation = validate();
        if(!validation) return;

        setLoading(true);

        const pendingMockupBase64 = mockupBase64;
        const pendingMockupDelete = mockupPendingDelete;
        const pendingMockupMetaToStore = pendingMockupMeta;
        let itemToStore = {
            budget_id: quote.id,
            subproduct_id: selectedSubproduct,
            company_id: selectedCompanyProduct,
            quantity: productQuantity,
            comments: productComments,
            calculation_type: productCalculationType,
            quantity_colors: productQuantityColors,
            cost: convertMoenyToInt( productUnitCost ),
            price: convertMoenyToInt( salePrice ),
            params: JSON.stringify( productParams ),
            customizations: getCustomizationsToStore()
        };
        
        api({
            method: create ? 'post' : 'put',
            url: create ? `/budget/item` : `/budget/item/${itemToEdit.id}`,
            data: itemToStore
        })
            .then(async (res) => {
                try {
                    const itemIdForMockup = res?.data?.resource?.id ?? itemToEdit?.id;
                    await applyPendingMockupAfterItemSave(
                        itemIdForMockup,
                        pendingMockupBase64,
                        pendingMockupDelete,
                        pendingMockupMetaToStore
                    );
                } catch (err) {
                    renderError(err);
                }
                updateQuote();
                if (create) {
                    toast.success('Produto adicionado com sucesso');
                } else {
                    close();
                }
            })
            .catch((error) => {
                renderError(error);
            })
            .finally(() => setLoading(false));
    }

    const validate = () => {
        let result = true;
        productParams.map(productParam => {
            if(productParam.value === null){
                let param = params.filter(param => param.id === productParam.id)[0];
                if(param.required){
                    toast.error(`O campo ${param.name} precisa ser informado`);
                    result = false;
                }
            }
        })

        return result;
    }

    const getCustomizationsToStore = () => {
        const intOrNull = value => (value === '' || value === null || value === undefined ? null : value);
        let customizationsToStore = [];
        productCustomizations.map(productCustomization => {
            customizationsToStore.push({
                customization_id: productCustomization.table === 'registered' ? productCustomization.selectedCustomization : null,
                company_id: productCustomization.selectedSupplierTypeCustomization === "PJ" ? productCustomization.selectedSupplierCustomization : null,
                customer_id: productCustomization.selectedSupplierTypeCustomization === "PF" ? productCustomization.selectedSupplierCustomization : null,
                quantity_colors: productCustomization.quantityColors,
                quantity: productCustomization.quantity,
                cost: convertMoenyToInt( productCustomization.customizationUnitCost ),
                total_cost: convertMoenyToInt( productCustomization.customizationTotalCost ),
                calculation_type: productCustomization.calculationType,
                supplier_selection_mode: productCustomization.supplierSelectionMode,
                table: productCustomization.table,
                spot_component: productCustomization?.spot_component ?? null,
                spot_location: productCustomization?.spot_location ?? null,
                spot_area: productCustomization?.spot_area ?? null,
                spot_customization: productCustomization?.spot_customization ?? null,
                spot_colors: productCustomization?.spot_colors ?? null,
                // campos do cálculo por tamanho (DTF)
                customization_size_id: intOrNull(productCustomization?.selectedSizeId),
                engravings_per_item: intOrNull(productCustomization?.engravingsPerItem),
                art_width_mm: intOrNull(productCustomization?.artWidthMm),
                art_height_mm: intOrNull(productCustomization?.artHeightMm),
                spacing_mm: intOrNull(productCustomization?.spacingMm),
                units: intOrNull(productCustomization?.units),
                fixed_fee: intOrNull(productCustomization?.fixedFee),
                application_cost: intOrNull(productCustomization?.applicationCost)
            });
        });
        return customizationsToStore;
    }

    const deleteProduct = () => {
        api.delete(`/budget/item/${itemToEdit.id}`).then(res => {
            getQuote();
            close();
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const isLoading = () => {
        return gettingCustomizations || loading;
    }

    const allDataLoaded =
        !gettingBillingCompanies && billingCompanies.length > 0 && (!itemToEdit || products.length > 0);

    const showMainForm = !gettingBillingCompanies && billingCompanies.length > 0;
    const sidebarVisible = showMainForm && !(itemToEdit && products.length === 0);

    // Não acoplar ao `gettingPrice`: ao trocar a cor o GET /subproduct/:id/search zera o meta e pode
    // deixar `colorPreviewUrl` vazio até a resposta — a URL da lista ou o default deve aparecer, não
    // o spinner indefinidamente. Imagem “atrasada” da busca já é evitada por subproductSearchMeta.subproductId.
    const sidebarColorImageLoading = Boolean(selectedSubproduct && itemToEdit && products.length === 0);

    const availablePricesLink = (text) => (
        <button type="button" className="link-available-prices" onClick={() => setShowAvailablePrices(true)}>
            {text}
        </button>
    );

    const productUnitCostErrorContent = productUnitCostError === COST_NOT_FOUND_ERROR
        ? <>{productUnitCostError} Ver {availablePricesLink('preços disponíveis')}.</>
        : productUnitCostError;

    const showMockupInlineSaving = showMockupInlineSavingEarly || mockupUploading;
    const hasSpotCustomizations =
        hasCalculationFeature && productCustomizations.some((customization) => customization.table === 'spot');
    // Lista vazia é resposta válida (produto sem componentes no Spot), não
    // "ainda carregando": exigir length > 0 prendia a seção de gravações no
    // spinner para sempre, já que nada mais busca o campo depois.
    const spotComponentsLoaded = Array.isArray(product?.spot_components);
    const showSpotCustomizationsLoading =
        hasSpotCustomizations && selectedProduct && !spotComponentsLoaded;


    return (
        <>
            <Modal
                className="modal--product-config"
                title={`Configuração do produto`}
                size={`xlarge`}
                show={true}
                close={close}
                footer={
                    allDataLoaded && (
                        <div className="modal-product-footer">
                            <div className="modal-product-footer__summary">
                                {!hasCalculationFeature && (
                                    <div className="modal-product-footer__field modal-product-footer__field--quantity">
                                        <Input label={`Quantidade`} value={productQuantity} change={setProductQuantity} mask={`convertToInt`} />
                                    </div>
                                )}
                                <div className="modal-product-footer__field modal-product-footer__field--price">
                                    <Input label={`Preço unit.`} value={isLoading() && hasCalculationFeature ? 'Calculando...' : salePrice} change={hasCalculationFeature ? undefined : setSalePrice} disabled={hasCalculationFeature} mask={!hasCalculationFeature ? 'convertToMoney' : undefined} />
                                </div>
                                <div className="modal-product-footer__field modal-product-footer__field--total">
                                    <Input label={`Total`} value={isLoading() && hasCalculationFeature ? 'Calculando...' : totalSalePrice} disabled={true} />
                                </div>
                            </div>
                            <div className="modal-product-footer__actions">
                                { hasPermissionToEditBudget && <Button type={`secondary`} svg={`plus-blue`} text={`Adicionar`} loading={loading} action={() => updateProduct(true)} /> }
                                {itemToEdit && hasPermissionToEditBudget &&
                                    <Button type={`primary`}  svg={`save-white`} loading={loading} text={`Atualizar`} action={() => updateProduct()} />
                                }
                            </div>
                        </div>
                    )
                }
            >
                {((itemToEdit && products.length === 0) || gettingBillingCompanies) && 
                    <p>Carregando...</p>
                }
                {!gettingBillingCompanies && billingCompanies.length === 0 &&
                    <p>Nenhuma empresa de faturamento encontrada</p>
                }
                {}
                {!gettingBillingCompanies && billingCompanies.length > 0 &&
                    <div
                        className={sidebarVisible ? 'modal-product-layout' : ''}
                        style={{
                            display:
                                itemToEdit && products.length === 0
                                    ? 'none'
                                    : sidebarVisible
                                      ? 'flex'
                                      : 'block'
                        }}
                    >
                    <div className={sidebarVisible ? 'modal-product-layout__main' : undefined} style={{ flex: sidebarVisible ? undefined : 1, minWidth: 0 }}>
                        <div className="section">
                            <SelectBillingCompany
                                selectedBillingCompany={selectedBillingCompany}
                                setSelectedBillingCompany={setSelectedBillingCompany}
                            />
                        </div>
                        <div className="modal-section-title">
                            <div className="left">
                                <h2>Produto</h2>
                            </div>
                            <div className="right">
                                {/* <InputRadio
                                    className={`no-margin`}
                                    options={calculationTypeOptions}
                                    value={productCalculationType}
                                    change={setProductCalculationType}
                                /> */}
                            </div>
                        </div>
                        <div className="section">
                            <div className="row">
                                <div className="col-4">
                                    <div style={{ position: 'relative', cursor: 'pointer' }}>
                                        <SelectProduct
                                            onlyQuotable={true}
                                            changeProducts={handleSearchProducts}
                                            selectedProduct={selectedProduct}
                                            setSelectedProduct={handleProductChange}
                                            selectedSubproductThumbnailUrl={selectedSubproductThumbnailUrl}
                                        />
                                       
                                        <div
                                            onClick={() => setShowModal(true)}
                                            style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            zIndex: 2,
                                            }}
                                        />
                                    </div>
                                    {selectedProduct &&
                                        <a href={`/product/edit/${selectedProduct}`} className="link-pos-input" target="_blank">Mais detalhes</a>
                                        }   
                                </div>
                                <div className="col-4">
                                    <InputSelect 
                                        label={`Cor`}
                                        options={createOptions(subproducts, 'desc', 'id')} 
                                        value={selectedSubproduct} 
                                        change={setSelectedSubproduct}
                                        isDisabled={subproducts[0] && subproducts[0].color === null}
                                    />
                                </div>
                                {hasCalculationFeature && canSeeProductSupplier() &&
                                    <div className="col-4">
                                        <Input
                                            label={`Fornecedor`}
                                            disabled={true}
                                            value={product?.company_trade_name ?? 'Sem informação'}
                                        />
                                    </div>
                                }
                                {hasCalculationFeature && (
                                    <div className="col-3">
                                        <Input 
                                            label={`Quantidade`}
                                            value={productQuantity}
                                            change={setProductQuantity}
                                            mask={`convertToInt`}
                                        />
                                        {canSeeStock() &&
                                            <span className="stock">{`Em estoque: ${stock.toLocaleString('pt-BR')}`}</span>
                                        }
                                    </div>
                                )}
                                {hasCalculationFeature && canSeeProductCost() &&
                                    <>
                                        <div className="col-3">
                                            <InputSelect
                                                label={`Origem do custo`}
                                                options={calculationTypeOptions}
                                                value={productCalculationType}
                                                change={setProductCalculationType}
                                                />
                                        </div>
                                    
                                        <div className="col-3">
                                            <Input
                                                label={`Custo unit.`}
                                                value={gettingPrice ? 'Buscando...' : productUnitCost}
                                                change={setProductUnitCost}
                                                mask={`convertToMoney`}
                                                disabled={productCalculationType === 'automatic'}
                                                error={productUnitCostErrorContent}
                                                hint={selectedSubproduct && !gettingPrice ? availablePricesLink('Preços disponíveis') : undefined}
                                                right={productCalculationType === 'automatic' && product && <ProductPriceAlert product={product} />}
                                            />
                                        </div>
                                        <div className="col-3">
                                            <Input
                                                label={`Custo total`}
                                                value={gettingPrice ? 'Buscando...' : productTotalCost}
                                                disabled={true}
                                            />
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                        
                        {showSpotCustomizationsLoading && (
                            <div className="section" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Spinner size={16} />
                                <span>Carregando gravacoes do produto...</span>
                            </div>
                        )}

                        {hasCalculationFeature && !showSpotCustomizationsLoading && productCustomizations.map((productCustomization, index) => (
                            <CustomizationSection
                                key={index}
                                index={index}
                                product={product}
                                calculationTypeOptions={calculationTypeOptions}
                                customizations={customizations}
                                productCustomization={productCustomization}
                                productCustomizations={productCustomizations}
                                setProductCustomizations={setProductCustomizations}
                                productQuantity={productQuantity}
                                productSupplierCost={productSupplierCost}
                            />
                        ))}

                        {hasCalculationFeature && !showSpotCustomizationsLoading && (customizations.length > 0 || product?.origin === 'integracao_spot') && product &&
                            <Button 
                                type={`link`}
                                size={`small`}
                                full={true}
                                text={`Adicionar gravação`}
                                action={addProductCustomization}
                            />
                        }

                        {hasCalculationFeature && params.map((param, index) => (
                            <ParamSection
                                key={index}
                                param={param}
                                productParams={productParams}
                                setProductParams={setProductParams}
                            />
                        ))}

                        <div className="modal-section-title">
                            <div className="left">
                                <h2>Observações</h2>
                            </div>
                        </div>
                        <div className="section">
                            <InputTextarea 
                                placeholder={`Escreva as observações adicionais do item aqui...`}
                                value={productComments}
                                change={setProductComments}
                            />
                        </div>
                    </div>

                    {sidebarVisible && (
                        <MockupPanel
                            hasProduct={!!selectedProduct}
                            productImageSrc={colorPreviewUrl}
                            productImageLoading={sidebarColorImageLoading}
                            mockupSrc={mockupDisplaySrc}
                            mockupIsPdf={mockupIsPdf}
                            mockupInfo={mockupInfo}
                            manualFileName={mockupBase64 ? mockupFileName : ''}
                            colorMismatch={Boolean(
                                mockupDisplaySrc &&
                                mockupInfo?.subproduct_id &&
                                selectedSubproduct &&
                                mockupInfo.subproduct_id !== selectedSubproduct
                            )}
                            saving={showMockupInlineSaving}
                            savingText={mockupUploading ? 'Enviando a amostra virtual...' : 'Gravando o item do orçamento e a amostra...'}
                            canEdit={canDropVirtualSample}
                            canGenerate={canDropVirtualSample && !!colorPreviewUrl}
                            canPersist={canPersistMockup(quote)}
                            canDownloadPdf={!!serverMockupUrl && !mockupBase64}
                            dropzone={{ getRootProps, getInputProps, isDragActive, open: openMockupFileDialog }}
                            onGenerate={() => confirmPlanFeature('product_mock').then(can => can ? setShowGenerateMockup(true) : setShowMockupUpgrade(true))}
                            onRemove={() => setShowMockupDelete(true)}
                            onDownloadPdf={downloadMockupPdf}
                        />
                    )}
                    </div>
                }
            </Modal>

            <ModalDelete
                action={confirmRemoveMockup}
                close={() => setShowMockupDelete(false)}
                show={showMockupDelete}
                text={
                    serverMockupUrl && canPersistMockup(quote) ? (
                        <p>
                            O mockup some da pré-visualização agora e será removido de fato ao clicar em{' '}
                            <strong>Adicionar</strong> ou <strong>Atualizar</strong>.
                        </p>
                    ) : (
                        <p>Remover o mockup selecionado da pré-visualização?</p>
                    )
                }
            />
            <ModalDelete
                action={deleteProduct}
                close={() => setShowDelete(false)}
                show={showDelete}
                loading={loading}
            />
            <ModalProductList
                close={() => setShowModal(false)}
                selectedProduct={selectedProduct}
                setSelectedProduct={handleProductChange}
                show={showModal}
                loading={loading}
            />
            <ModalAvailablePrices
                show={showAvailablePrices}
                close={() => setShowAvailablePrices(false)}
                prices={subproductPrices}
                quantity={productQuantity}
                product={product}
            />
            <ModalGenerateMockup
                show={showGenerateMockup}
                close={() => setShowGenerateMockup(false)}
                baseImageUrl={colorPreviewUrl}
                imageOptions={mockupImageOptions}
                productName={product?.name ?? null}
                productDescription={product?.description ?? null}
                quoteId={quote?.id ?? null}
                itemId={mockupItemId}
                productId={selectedProduct}
                subproductId={selectedSubproduct}
                companyId={quote?.company_id ?? null}
                customerId={quote?.customer_id ?? null}
                onApply={applyGeneratedMockup}
            />

            <PlanFeatureUpgradeModal
                feature={`product_mock`}
                show={showMockupUpgrade}
                close={() => setShowMockupUpgrade(false)}
            />
        </>
    );
}

export default ModalProduct;
